// LoRaWAN sensor provisioning: add/edit form and CSV bulk import. The API
// keeps ChirpStack in step; the AppKey is write-only (sent, never shown again).
import { useState, type SyntheticEvent } from 'react';
import type { LorawanDevice, LorawanProfiles } from '../api/types.js';
import { useApi } from '../auth.js';
import { ErrorBox, Spinner } from '../components.js';
import { useResource } from '../hooks.js';
import {
  appKeyError,
  devEuiError,
  importSensors,
  joinEuiError,
  normalizeHex,
  parseSensorCsv,
  profileOptions,
  type ImportResult,
  type ImportRow,
} from '../lib/lorawan.js';

export const DEFAULT_PROFILE = 'elsys-ers';

function useProfiles(siteId: string) {
  const api = useApi();
  return useResource(`lorawan-profiles:${siteId}`, (s) => api.lorawanProfiles(siteId, s));
}

function ProfileSelect({
  profiles,
  value,
  onChange,
  disabled,
}: {
  profiles: LorawanProfiles | undefined;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  const options = profileOptions(profiles);
  return (
    <select
      value={value}
      disabled={disabled}
      onChange={(e) => {
        onChange(e.target.value);
      }}
    >
      {options.length === 0 && <option value={value}>{value}</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function HexField({
  label,
  value,
  onChange,
  error,
  placeholder,
  secret,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  error: string | null;
  placeholder: string;
  secret?: boolean;
}) {
  const show = value !== '' && error !== null;
  return (
    <label className="field">
      <span>{label}</span>
      <input
        type={secret ? 'password' : 'text'}
        className={`mono${show ? ' invalid' : ''}`}
        autoComplete="off"
        spellCheck={false}
        placeholder={placeholder}
        value={value}
        aria-invalid={show}
        onChange={(e) => {
          onChange(e.target.value);
        }}
      />
      {show && <span className="form-error">{error}</span>}
    </label>
  );
}

/** Add a sensor, or edit one (`device` set): name, description, profile, optional new AppKey. */
export function SensorForm({
  siteId,
  device,
  onDone,
  onCancel,
}: {
  siteId: string;
  device?: LorawanDevice;
  onDone: () => void;
  onCancel: () => void;
}) {
  const api = useApi();
  const profiles = useProfiles(siteId);
  const editing = device !== undefined;
  const [devEui, setDevEui] = useState('');
  const [joinEui, setJoinEui] = useState('0000000000000000');
  const [appKey, setAppKey] = useState('');
  const [name, setName] = useState(device?.name ?? '');
  const [description, setDescription] = useState(device?.description ?? '');
  const initialProfile = device?.profile_id ?? DEFAULT_PROFILE;
  const [profile, setProfile] = useState(initialProfile);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const errors = editing
    ? { devEui: null, joinEui: null, appKey: appKey === '' ? null : appKeyError(appKey) }
    : { devEui: devEuiError(devEui), joinEui: joinEuiError(joinEui), appKey: appKeyError(appKey) };
  const valid =
    name.trim() !== '' &&
    errors.devEui === null &&
    errors.joinEui === null &&
    errors.appKey === null;

  async function submit(e: SyntheticEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      if (device) {
        await api.updateLorawanDevice(siteId, device.dev_eui, {
          name: name.trim(),
          description: description.trim(),
          ...(profile !== initialProfile && { profile }),
          ...(appKey !== '' && { app_key: normalizeHex(appKey) }),
        });
      } else {
        await api.createLorawanDevice(siteId, {
          dev_eui: normalizeHex(devEui),
          join_eui: normalizeHex(joinEui),
          app_key: normalizeHex(appKey),
          name: name.trim(),
          description: description.trim(),
          profile,
        });
      }
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="card edge-form"
      onSubmit={(e) => {
        void submit(e);
      }}
    >
      <h2>{device ? `Edit ${device.name ?? device.dev_eui}` : 'Add sensor'}</h2>
      {device ? (
        <p className="muted small">
          DevEUI <span className="mono">{device.dev_eui}</span>. To change the DevEUI, delete the
          sensor and add it again.
        </p>
      ) : (
        <p className="muted small">
          OTAA, EU868. Values from the vendor’s delivery sheet or QR code; dashes, colons and spaces
          are fine.
        </p>
      )}
      {!device && (
        <div className="edge-form-row">
          <HexField
            label="DevEUI"
            value={devEui}
            onChange={setDevEui}
            error={errors.devEui}
            placeholder="70B3D57ED0000001"
          />
          <HexField
            label="JoinEUI (AppEUI)"
            value={joinEui}
            onChange={setJoinEui}
            error={errors.joinEui}
            placeholder="0000000000000000"
          />
        </div>
      )}
      <HexField
        label={device ? 'New AppKey (leave empty to keep the current one)' : 'AppKey'}
        value={appKey}
        onChange={setAppKey}
        error={errors.appKey}
        placeholder="32 hex digits"
        secret
      />
      <p className="muted small">
        The AppKey goes to ChirpStack only; Kalvio never shows it again.
      </p>
      <div className="edge-form-row">
        <label className="field">
          <span>Name</span>
          <input
            type="text"
            maxLength={100}
            placeholder="e.g. E214 room sensor"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
        </label>
        <label className="field">
          <span>Device profile</span>
          <ProfileSelect
            profiles={profiles.data}
            value={profile}
            onChange={setProfile}
            disabled={!profiles.data}
          />
        </label>
      </div>
      <label className="field">
        <span>Description (optional)</span>
        <input
          type="text"
          maxLength={500}
          placeholder="e.g. Bedroom, above the door"
          value={description}
          onChange={(e) => {
            setDescription(e.target.value);
          }}
        />
      </label>
      {profiles.error && <ErrorBox error={profiles.error} onRetry={profiles.reload} />}
      {error && <ErrorBox error={error} />}
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={busy || !valid}>
          {busy ? 'Saving…' : device ? 'Save' : 'Add sensor'}
        </button>
        <button type="button" className="btn" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

const CSV_EXAMPLE = `DevEUI;JoinEUI;AppKey;Name;Profile
A81758FFFE0312AB;0000000000000000;2B7E151628AED2A6ABF7158809CF4F3C;E214 room sensor;elsys-ers`;

/** CSV bulk import: paste or pick a file, preview with per-row problems, then create row by row. */
export function SensorImport({
  siteId,
  existing,
  onDone,
  onCancel,
}: {
  siteId: string;
  existing: ReadonlySet<string>;
  onDone: () => void;
  onCancel: () => void;
}) {
  const api = useApi();
  const profiles = useProfiles(siteId);
  const [text, setText] = useState('');
  const [defaultProfile, setDefaultProfile] = useState(DEFAULT_PROFILE);
  const [results, setResults] = useState<Map<number, ImportResult>>(new Map());
  const [running, setRunning] = useState(false);
  const rows: ImportRow[] = parseSensorCsv(text, {
    profiles: profiles.data,
    defaultProfile,
    existing,
  });
  const ready = rows.filter((r) => r.errors.length === 0 && !results.get(r.line)?.ok);
  const done = [...results.values()];

  async function run() {
    setRunning(true);
    try {
      await importSensors(
        ready,
        (s) => api.createLorawanDevice(siteId, s),
        (r) => {
          setResults((m) => new Map(m).set(r.line, r));
        },
      );
    } finally {
      setRunning(false);
      onDone();
    }
  }

  async function pick(file: File | undefined) {
    if (!file) return;
    setText(await file.text());
    setResults(new Map());
  }

  return (
    <div className="card edge-form">
      <h2>Import sensors (CSV)</h2>
      <p className="muted small">
        One sensor per line: <span className="mono">DevEUI;JoinEUI;AppKey;Name;Profile</span>. The
        profile is a catalog key (<span className="mono">elsys-ers</span>,{' '}
        <span className="mono">cayenne-lpp</span>, <span className="mono">raw</span>) or a profile
        name; empty takes the default below. A header line is skipped.
      </p>
      <div className="edge-form-row">
        <label className="field">
          <span>File</span>
          <input
            type="file"
            accept=".csv,.txt,text/csv,text/plain"
            onChange={(e) => {
              void pick(e.target.files?.[0]);
            }}
          />
        </label>
        <label className="field">
          <span>Default profile</span>
          <ProfileSelect
            profiles={profiles.data}
            value={defaultProfile}
            onChange={setDefaultProfile}
            disabled={!profiles.data || running}
          />
        </label>
      </div>
      <label className="field">
        <span>…or paste</span>
        <textarea
          className="mono"
          rows={6}
          spellCheck={false}
          placeholder={CSV_EXAMPLE}
          value={text}
          disabled={running}
          onChange={(e) => {
            setText(e.target.value);
            setResults(new Map());
          }}
        />
      </label>
      {profiles.error && <ErrorBox error={profiles.error} onRetry={profiles.reload} />}
      {!profiles.data && !profiles.error && <Spinner label="Loading device profiles…" />}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table table-compact">
            <thead>
              <tr>
                <th className="num">Line</th>
                <th>DevEUI</th>
                <th>Name</th>
                <th>Profile</th>
                <th>Check / result</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const res = results.get(r.line);
                return (
                  <tr key={r.line}>
                    <td className="num">{r.line}</td>
                    <td className="mono">{r.sensor.dev_eui || '–'}</td>
                    <td>{r.sensor.name || '–'}</td>
                    <td className="mono small">{r.sensor.profile || '–'}</td>
                    <td>
                      {r.errors.length > 0 ? (
                        <span className="bad small">{r.errors.join('; ')}</span>
                      ) : res ? (
                        res.ok ? (
                          <span className="small">Added</span>
                        ) : (
                          <span className="bad small">{res.error}</span>
                        )
                      ) : (
                        <span className="muted small">Ready</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {done.length > 0 && (
        <p className="small" role="status">
          {done.filter((r) => r.ok).length} added, {done.filter((r) => !r.ok).length} failed.
        </p>
      )}
      <div className="form-actions">
        <button
          type="button"
          className="btn btn-primary"
          disabled={running || ready.length === 0 || !profiles.data}
          onClick={() => {
            void run();
          }}
        >
          {running
            ? 'Importing…'
            : `Import ${String(ready.length)} sensor${ready.length === 1 ? '' : 's'}`}
        </button>
        <button type="button" className="btn" disabled={running} onClick={onCancel}>
          Close
        </button>
      </div>
    </div>
  );
}
